import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';

export type CurrencyBalance = {
  availableCents: number;
  currency: string;
};

export const isMultiCurrencyInternalTransfer = ({
  transferType,
  enableMultiCurrencyDepositTransfers,
}: {
  transferType?: string;
  enableMultiCurrencyDepositTransfers: boolean;
}): boolean =>
  enableMultiCurrencyDepositTransfers &&
  transferType === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER;

export const getSubAccountCurrencies = (
  subAccount?: Partial<SubAccount>,
): CurrencyCodes[] => {
  if (!subAccount?.account) return [];
  return (
    subAccount.account.availableCurrencies || [
      subAccount.account.baseCurrency || CurrencyCodes.CAD,
    ]
  );
};

export const getAvailableCurrenciesForTransfer = ({
  applicableData,
}: {
  applicableData?: Pick<
    TransferContextData,
    'fromSubAccount' | 'subAccount'
  > | null;
}) =>
  getSharedCurrencies(
    applicableData?.fromSubAccount,
    applicableData?.subAccount,
  );

export const sharesCurrencyWithSelection = ({
  subAccount,
  selectedSubAccount,
  enableMultiCurrencyDepositTransfers,
}: {
  subAccount: SubAccount;
  selectedSubAccount: Partial<SubAccount> | undefined;
  enableMultiCurrencyDepositTransfers: boolean;
}): boolean => {
  if (!enableMultiCurrencyDepositTransfers || !selectedSubAccount?.account) {
    return true;
  }

  return getSharedCurrencies(selectedSubAccount, subAccount).length > 0;
};

export const shouldClearCounterpartSubAccount = ({
  selectedSubAccount,
  counterpartSubAccount,
  enableMultiCurrencyDepositTransfers,
}: {
  selectedSubAccount: Pick<SubAccount, 'id' | 'account'>;
  counterpartSubAccount: Partial<SubAccount> | undefined;
  enableMultiCurrencyDepositTransfers: boolean;
}) => {
  const hasMatchingCurrencies =
    counterpartSubAccount?.account?.availableCurrencies?.every((currency) =>
      selectedSubAccount.account.availableCurrencies?.includes(currency),
    );

  return (
    counterpartSubAccount?.id === selectedSubAccount.id ||
    (!hasMatchingCurrencies && !enableMultiCurrencyDepositTransfers)
  );
};

export const hasInsufficientCurrencyBalance = ({
  isMultiCurrencyInternalTransfer,
  applicableData,
  currencyBalance,
}: {
  isMultiCurrencyInternalTransfer: boolean;
  applicableData?: Pick<TransferContextData, 'currency' | 'amountCents'> | null;
  currencyBalance: CurrencyBalance;
}) =>
  isMultiCurrencyInternalTransfer &&
  !!applicableData?.currency &&
  (applicableData?.amountCents || 0) > currencyBalance.availableCents;

const getSharedCurrencies = (
  subAccount?: Partial<SubAccount>,
  otherSubAccount?: Partial<SubAccount>,
) => {
  const otherCurrencies = getSubAccountCurrencies(otherSubAccount);
  return getSubAccountCurrencies(subAccount).filter((currency) =>
    otherCurrencies.includes(currency),
  );
};
